import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { memberPath } from '@/config/hosts';
import { getSubscriptionState, isAdmin } from '@/utils/subscription';
import ProPaywallModal from '@/components/ProPaywallModal';

export default function AppAuthGate({ children }) {
  const { user, isAuthenticated, loading: authLoading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [checkingSub, setCheckingSub] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  const returnUrl = location.pathname + location.search;

  const checkAccess = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setCheckingSub(false);
      setHasAccess(false);
      return;
    }
    if (isAdmin(user)) {
      setHasAccess(true);
      setCheckingSub(false);
      return;
    }
    try {
      setCheckingSub(true);
      const state = await getSubscriptionState(user);
      if (state && state.isActive) {
        setHasAccess(true);
      } else {
        setHasAccess(false);
      }
    } catch (err) {
      console.error('Subscription verification failed:', err);
      setHasAccess(false);
    } finally {
      setCheckingSub(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (!authLoading) {
      checkAccess();
    }
  }, [authLoading, checkAccess]);

  if (authLoading || (isAuthenticated && checkingSub)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f6f4ef' }}>
        <p style={{ color: '#6d6a63' }}>Checking your access…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    const next = location.pathname + location.search;
    const redirect = next.startsWith('/app') ? next : memberPath(next);
    return <Navigate to={`/authentication?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ background: '#f6f4ef' }}>
        <div className="mb-4 text-center">
          <p className="text-xs text-[#6d6a63]">
            Signed in as <span className="font-semibold text-[#161616]">{user?.email}</span> ·{' '}
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate('/');
              }}
              className="underline text-[#0f3e17] font-medium hover:text-[#1a4a21]"
            >
              Sign out
            </button>
          </p>
        </div>
        <ProPaywallModal
          open={true}
          returnPath={returnUrl}
          title="Choose a plan to enter ByOnco"
          subtitle="Full access to oncology companion care, expert second opinions, hospital matching, and voice."
          onClose={() => navigate('/')}
          onUnlocked={checkAccess}
        />
      </div>
    );
  }

  return children;
}
